/**
 * The Reschedule button.
 *
 * One button in the UI, and the most consequential one in the app: it throws
 * away the plan and lays the next stretch of days out again. All of that lived
 * inline in `useSchedule.reschedule` — a React `useCallback` — which meant
 * nothing could test it and nothing else could call it. It also carried its
 * own copy of the paused-project walk that `pausedProjectIds` already does,
 * which is how two versions of "is this project paused" came to exist.
 *
 * Everything that decides anything now lives here: which tasks are eligible,
 * how many days to lay out, and the carry-forward that stops a task being
 * scheduled twice. The callers supply data and persist the result; they make
 * no scheduling decisions of their own.
 *
 * Data access is injected rather than imported, because the two callers reach
 * it differently — the app has an IndexedDB adapter and a calendar cache, the
 * MCP server has a JSON export and no calendar at all (CLAUDE.md §3.6: event
 * titles never leave the device, so the server genuinely cannot see them).
 * Injecting keeps this file free of both.
 */
import { Scheduler } from './scheduler';
import { dormantProjectIds } from './unscheduled';
import { habitPausedOn } from './habit-schedule';
import { toISODateString } from '../utils/date-utils';
/** Tasks the scheduler is allowed to place. */
export function schedulableTasks(tasks, projects, 
/** The first day being planned — a pause that ended before it doesn't hold. */
today = toISODateString(new Date())) {
    /**
     * Paused, archived AND completed projects are all skipped.
     *
     * Until v1.70 only `paused` was, so archiving a project removed it from
     * every picker while its tasks kept appearing in the plan — work from
     * something the user had explicitly finished with, arriving in their day
     * with no project on screen to trace it back to.
     */
    const dormant = dormantProjectIds(projects);
    return tasks.filter((t) => t.status !== 'completed'
        && !habitPausedOn(t, today)
        && !t.is_archived
        // Subtasks are placed with their parent, never on their own.
        && !t.parent_task_id
        && !dormant.has(t.project_id));
}
const TASKS_PER_DAY = 6;
const BUFFER_DAYS = 3;
const MAX_DAYS = 90;
/**
 * How far ahead to lay out.
 *
 * Habits recur every day and would make any count look enormous, so only
 * real tasks drive the estimate. The buffer stops the last day being packed
 * to the minute, and the clamp stops a large backlog generating a year of
 * schedules nobody will look at.
 */
export function daysToPlan(tasks) {
    const oneOff = tasks.filter((t) => !t.is_habit).length;
    return Math.min(MAX_DAYS, Math.max(1, Math.ceil(oneOff / TASKS_PER_DAY) + BUFFER_DAYS));
}
/**
 * Lay out the plan. Returns the schedules to save; saves nothing itself.
 */
export async function reschedulePlan(req) {
    const scheduler = new Scheduler({ preferences: req.preferences, currentTime: req.currentTime });
    const start = req.startDate ?? toISODateString(new Date());
    const active = schedulableTasks(req.tasks, req.projects, start);
    const days = req.days ?? daysToPlan(active);
    const schedules = [];
    /**
     * A one-off task placed on Monday must not also be placed on Tuesday.
     * Habits are exempt — recurring by definition — which is why only
     * non-habits are recorded here.
     */
    const alreadyPlaced = new Set();
    for (let offset = 0; offset < days; offset++) {
        const date = new Date(`${start}T00:00:00`);
        date.setDate(date.getDate() + offset);
        const targetDate = toISODateString(date);
        const generated = scheduler.generateSchedule({
            targetDate,
            tasks: active,
            // Always passed, even when forcing: `force` controls regeneration, not
            // whether completions are preserved. Dropping it would erase what the
            // user already ticked off today.
            existingSchedule: req.scheduleFor(targetDate),
            force: req.reschedule_all,
            previouslyScheduledIds: alreadyPlaced,
            habitInstancesForDate: await req.habitInstancesFor(targetDate),
            externalBusy: req.externalBusyFor?.(targetDate),
        });
        for (const placed of generated.scheduled_tasks ?? []) {
            if (placed.is_buffer || !placed.task_id)
                continue;
            const task = active.find((t) => t.id === placed.task_id);
            if (task && !task.is_habit)
                alreadyPlaced.add(placed.task_id);
        }
        schedules.push(generated);
    }
    return schedules;
}
//# sourceMappingURL=reschedule-plan.js.map